import type { EnvironmentThreadStatus } from "@afar-research/client-runtime/state/threads";

export interface UnreadableThreadPresentation {
  /** Titlebar text, so the user can see which thread is refusing to open. */
  readonly heading: string;
  /** Omitted while the thread is merely still loading, to keep a switch quiet. */
  readonly title: string | null;
  /** Screen-reader text; drawn visibly only when the thread is not merely loading. */
  readonly detail: string;
  /** Draw the conversation's placeholder shape instead of a message. */
  readonly loading: boolean;
  /** `null` when nothing the client can do would bring the thread back. */
  readonly retryLabel: string | null;
}

/**
 * What to say about a thread the client has selected but cannot read, while its
 * Environment is reachable.
 *
 * This is the counterpart of the no-thread-selected empty state: the user picked this
 * thread, so the surface owes them the reason and a way back in. An Environment that is
 * unreachable is a different answer and belongs to `ThreadUnavailableState`.
 */
export function presentUnreadableThread(input: {
  readonly threadTitle: string | null;
  readonly status: EnvironmentThreadStatus;
  readonly error: string | null;
}): UnreadableThreadPresentation {
  const heading = input.threadTitle?.trim() || "Thread";
  if (input.status === "deleted") {
    return {
      heading,
      title: "This thread is gone",
      detail: "The Environment no longer has this thread, so there is nothing left to open.",
      retryLabel: null,
      loading: false,
    };
  }
  if (input.error !== null) {
    return {
      heading,
      title: "This thread could not be loaded",
      detail: `${input.error} AFAR keeps trying on its own, and you can try again now.`,
      retryLabel: "Try again",
      loading: false,
    };
  }
  return {
    heading,
    title: null,
    detail: "Loading this thread…",
    retryLabel: "Try again",
    loading: true,
  };
}
